import { Modal, StackModalProps, useModals } from 'almanac-ui/Modal'
import cx from 'classnames'
import Styles from 'screens/BrandKits/components/BrandVoice/AddVoice/AddVoice.module.scss'
import { ArrowLeft, Play3 } from 'blaze-ui/icons'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'
import { useTutorials } from 'hooks/queries/useTutorials'
import { TutorialCategory } from 'types'
import { VideoModal } from 'components/VideoModal/VideoModal'
import { AnalyseContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/AnalyseContentModal'
import { AddVoiceModalProps } from './Types'
import { AnalysingContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/AnalysingContentModal'
import { EditVoiceModal } from 'screens/BrandKits/components/BrandVoice/EditVoiceModal'
import { CreateContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/CreateContentModal'

export const IntroModal = ({ close, onComplete, onSuccess }: AddVoiceModalProps & Pick<StackModalProps, 'close'>) => {
  const { closeModal: closeAlmanacModal, openModal } = useModals()
  const { data } = useTutorials({ categories: TutorialCategory.QuickQuest }, { cacheTime: 60 * 60 * 15 })
  const voiceTutorial = data?.tutorials?.filter((tut) => tut.title === 'How to create a brand voice')[0]
  const { isMobile } = useResponsiveDesign()
  const handleOpenVideo = () => {
    if (!voiceTutorial) return
    openModal(VideoModal, { embedId: voiceTutorial?.video_id })
  }

  const closeModal = (voiceId?: number) => {
    onComplete?.(voiceId)

    close()
  }

  return (
    <Modal.Root
      size="sm"
      blaze
      withDefaultBg={isMobile}
      onClose={closeModal}
      onPressOutside={closeModal}
      aria-labelledby="step-heading"
    >
      {isMobile && (
        <div className={cx(Styles.minimalHeader, Styles.noMargin)}>
          <div onClick={closeAlmanacModal}>
            <ArrowLeft size={20} color="var(--light-100)" />
          </div>
          <Heading level={5} color="var(--light-100)">
            Brand Voice
          </Heading>
        </div>
      )}
      <div className={Styles.heroImgContainer} />
      <Modal.Content blaze>
        <div className={Styles.container}>
          <Heading level={3} id="step-heading">
            {isMobile
              ? 'Create your first voice to generate on-brand content'
              : "Written content that's always on brand"}
          </Heading>
          <Text color="var(--dark-60)">
            {isMobile
              ? 'Blaze will analyze your unique style and tone from your website or docs to create a reusable Brand Voice.'
              : 'Brand voices ensure Blaze content sounds like your business every time. Let Blaze set up a brand for you in just a few clicks.'}
          </Text>
        </div>
      </Modal.Content>
      <Modal.Footer blaze sticky={isMobile} blur={isMobile} withContentBackground={isMobile}>
        <Modal.FooterContent slot="left">
          {!isMobile && (
            <Button onPress={() => closeAlmanacModal()} size="lg" variant="tertiary">
              Cancel
            </Button>
          )}
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.buttonsRow}>
            {voiceTutorial && !isMobile && (
              <Button frontIcon={Play3} size="lg" variant="tertiary" onPress={handleOpenVideo}>
                Learn How
              </Button>
            )}
            <Button
              size="lg"
              onPress={() =>
                openModal(AnalyseContentModal, {
                  onSuccess,
                  onComplete,
                  onAnalyse: (props) =>
                    openModal(AnalysingContentModal, {
                      onSuccess,
                      onComplete,
                      ...props,
                      onNext: (props) =>
                        openModal(EditVoiceModal, {
                          ...props,
                          onBack: () => {
                            closeModal()
                            closeModal()
                          },
                          onCreate: (createProps) => openModal(CreateContentModal, createProps),
                        }),
                    }),
                })
              }
            >
              {isMobile ? 'Get Started' : 'Create Brand Voice'}
            </Button>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
